import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Key as KeyIcon,
  OpenInNew as OpenInNewIcon,
  VpnKey as VpnKeyIcon,
} from "@mui/icons-material";
import { useForm } from "react-hook-form";
import { Contract } from "fhir/r5";
import { toast } from "react-toastify";
import { FormTextInput } from "@/components/form/FormTextInput";
import { FormButtonGroup } from "@/components/form/FormButtonGroup";
import { useMedicalReportsAgentContext } from "@/contexts/MedicalReportsAgentContext";
import { useGuidanceAgentContext } from "@/contexts/GuidanceAgentContext";
import { useDiscussionCaseContext } from "@/contexts/DiscussionCaseContext";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useSessionContext } from "@/contexts/SessionContext";
import { PostContract } from "@/services/fhir/contract/PostContract";
import { validateApiKey as validateProviderApiKey } from "@/services/aiKeyValidation";
import {
  getApiKeyFromContract,
  getProviderSlugFromContract,
} from "../helpers/activationInfo";
import {
  AIProviderSlug,
  maskApiKey,
  providerConfigs,
} from "../helpers/aiProviderDisplay";
import { buildContractBody } from "./contractBody";

// ---------------------------------------------------------------------------
// Tipos e constantes locais da feature (não exportar — só este modal usa)
// ---------------------------------------------------------------------------

type AgentIdentifier =
  | "MEDICAL_REPORTS_AGENT"
  | "GUIDANCE_AGENT"
  | "DISCUSSION_CASE_AGENT";

type WizardStep = "provider" | "credential";
type CredentialMode = "reuse" | "new";

const AGENT_LABELS: Record<AgentIdentifier, string> = {
  DISCUSSION_CASE_AGENT: "Agente de Discussão de Casos",
  MEDICAL_REPORTS_AGENT: "Agente de Relatórios Médicos",
  GUIDANCE_AGENT: "Agente de Orientações ao Paciente",
};

/** Monta lista de chaves já cadastradas em outros agentes (mesmo provedor). */
function getReuseKeysFromOtherAgents(
  currentAgent: AgentIdentifier,
  currentProvider: AIProviderSlug,
  contracts: {
    discussionCase: Contract | null | undefined;
    medicalReports: Contract | null | undefined;
    guidance: Contract | null | undefined;
  }
) {
  const otherAgents = (
    [
      {
        identifier: "DISCUSSION_CASE_AGENT",
        extensionSlug: "discussion_case_agent",
        label: AGENT_LABELS.DISCUSSION_CASE_AGENT,
        contract: contracts.discussionCase,
      },
      {
        identifier: "MEDICAL_REPORTS_AGENT",
        extensionSlug: "medical_reports_agent",
        label: AGENT_LABELS.MEDICAL_REPORTS_AGENT,
        contract: contracts.medicalReports,
      },
      {
        identifier: "GUIDANCE_AGENT",
        extensionSlug: "guidance_agent",
        label: AGENT_LABELS.GUIDANCE_AGENT,
        contract: contracts.guidance,
      },
    ] as const
  ).filter((agent) => agent.identifier !== currentAgent);

  const entries = otherAgents
    .map((agent) => {
      const apiKey = getApiKeyFromContract(agent.contract, agent.extensionSlug);
      const provider = getProviderSlugFromContract(
        agent.contract,
        agent.extensionSlug
      );
      if (!apiKey || !provider || provider !== currentProvider) return null;
      return { label: agent.label, value: apiKey };
    })
    .filter((item): item is { label: string; value: string } => Boolean(item));

  // Agrupa chaves iguais (mesma API key em mais de um agente)
  const grouped: { value: string; usedIn: string[] }[] = [];
  for (const entry of entries) {
    const existing = grouped.find((g) => g.value === entry.value);
    if (existing) {
      if (!existing.usedIn.includes(entry.label)) existing.usedIn.push(entry.label);
    } else {
      grouped.push({ value: entry.value, usedIn: [entry.label] });
    }
  }
  return grouped;
}

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------

export interface ConfigureAIModalProps {
  open: boolean;
  onClose: () => void;
  identifier: AgentIdentifier;
}

export const ConfigureAIModal: React.FC<ConfigureAIModalProps> = ({
  open,
  onClose,
  identifier,
}) => {
  const [step, setStep] = useState<WizardStep>("provider");
  const [credentialMode, setCredentialMode] = useState<CredentialMode | null>(
    null
  );
  const [redirectConfirmOpen, setRedirectConfirmOpen] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    formState: { isSubmitting },
    reset,
    setValue,
  } = useForm<{ provider: AIProviderSlug; apiKey: string }>({
    defaultValues: { provider: "openai", apiKey: "" },
  });

  const { organizationId } = useOrganizationContext();
  const { user } = useSessionContext();
  const {
    refetchContract: refetchDiscussionCase,
    contractDiscussionCase,
  } = useDiscussionCaseContext();
  const {
    refetchContract: refetchMedicalReports,
    contractMedicalReports,
  } = useMedicalReportsAgentContext();
  const {
    refetchContract: refetchGuidance,
    contractGuidance,
  } = useGuidanceAgentContext();

  const currentProvider = watch("provider");
  const currentApiKey = watch("apiKey");
  const selectedProviderConfig = providerConfigs.find(
    (p) => p.slug === currentProvider
  );

  const reuseKeys = getReuseKeysFromOtherAgents(
    identifier,
    currentProvider,
    {
      discussionCase: contractDiscussionCase,
      medicalReports: contractMedicalReports,
      guidance: contractGuidance,
    }
  );

  const resetCredential = () => {
    setCredentialMode(null);
    setValue("apiKey", "");
  };

  // Ao abrir: refetch dos contratos + reset do wizard
  useEffect(() => {
    if (!open) return;

    void Promise.all([
      refetchDiscussionCase?.(),
      refetchMedicalReports?.(),
      refetchGuidance?.(),
    ]);

    reset({ provider: "openai", apiKey: "" });
    setStep("provider");
    setCredentialMode(null);
    setRedirectConfirmOpen(false);
  }, [
    open,
    reset,
    refetchDiscussionCase,
    refetchMedicalReports,
    refetchGuidance,
  ]);

  // Limpa chave reuse se não for mais válida para o provedor atual
  useEffect(() => {
    if (credentialMode !== "reuse" || !currentApiKey) return;
    const stillValid = reuseKeys.some((k) => k.value === currentApiKey);
    if (!stillValid) setValue("apiKey", "", { shouldValidate: true });
  }, [credentialMode, currentApiKey, reuseKeys, setValue]);

  const handleProviderSelect = (provider: AIProviderSlug) => {
    setValue("provider", provider);
    resetCredential();
    setStep("credential");
  };

  const handleCredentialModeChange = (mode: CredentialMode) => {
    setCredentialMode(mode);
    setValue("apiKey", "", { shouldValidate: true });
  };

  const handleNewKeyClick = () => {
    handleCredentialModeChange("new");
    if (selectedProviderConfig?.apiKeyUrl) setRedirectConfirmOpen(true);
  };

  const handleBack = () => {
    if (step === "credential") {
      setStep("provider");
      resetCredential();
    } else {
      onClose();
    }
  };

  const onSubmit = async (data: { provider: AIProviderSlug; apiKey: string }) => {
    const refetch =
      identifier === "DISCUSSION_CASE_AGENT"
        ? refetchDiscussionCase
        : identifier === "MEDICAL_REPORTS_AGENT"
          ? refetchMedicalReports
          : refetchGuidance;

    try {
      const validation = await validateProviderApiKey(
        data.provider,
        data.apiKey.trim()
      );
      if (!validation.isValid) {
        throw new Error(validation.error || "Chave de API inválida");
      }

      await PostContract({
        data: buildContractBody({
          provider: data.provider,
          apiKey: data.apiKey.trim(),
          cpf: user?.username,
          nome: user?.name,
          email: user?.email?.[0],
          organizationId,
          identifier,
        }),
      });

      onClose();
      toast.success("Ativação realizada com sucesso!");
      refetch?.();
    } catch {
      toast.error(
        "Erro ao validar a chave de API, verifique se a chave está correta"
      );
    }
  };

  const canSubmit =
    credentialMode !== null && currentApiKey.trim().length > 0;
  const hasReuseKeys = reuseKeys.length > 0;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
            <Typography variant="h6" component="span" fontWeight={600}>
              Ativar {AGENT_LABELS[identifier]}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5, display: "block" }}
            >
              Configure o provedor de IA e a chave de API
            </Typography>
          </DialogTitle>

          <DialogContent sx={{ pt: 1 }}>
            <Stepper activeStep={step === "provider" ? 0 : 1} sx={{ mb: 3 }}>
              <Step>
                <StepLabel>Escolher provedor</StepLabel>
              </Step>
              <Step>
                <StepLabel>Configurar credencial</StepLabel>
              </Step>
            </Stepper>

            {/* ---- Passo 1: Escolher provedor ---- */}
            {step === "provider" && (
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  Selecione o provedor de inteligência artificial que deseja
                  utilizar:
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  {providerConfigs.map((config) => {
                    const isSelected = currentProvider === config.slug;
                    return (
                      <Card
                        key={config.slug}
                        variant="outlined"
                        sx={{
                          flex: 1,
                          borderColor: isSelected
                            ? "primary.main"
                            : "neutral700.main",
                          borderWidth: isSelected ? 2 : 1,
                          transition: "border-color 0.2s",
                        }}
                      >
                        <CardActionArea
                          onClick={() => handleProviderSelect(config.slug)}
                          sx={{ minHeight: 44 }}
                        >
                          <CardContent sx={{ p: 2 }}>
                            <Stack
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                              sx={{ mb: 1 }}
                            >
                              <Typography variant="subtitle1" fontWeight={600}>
                                {config.label}
                              </Typography>
                              {isSelected && (
                                <CheckCircleIcon
                                  color="primary"
                                  fontSize="small"
                                />
                              )}
                            </Stack>
                            <Typography variant="body2" color="text.secondary">
                              {config.description}
                            </Typography>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    );
                  })}
                </Stack>
              </Stack>
            )}

            {/* ---- Passo 2: Configurar credencial ---- */}
            {step === "credential" && selectedProviderConfig && (
              <Stack spacing={2.5}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    p: 1.5,
                    bgcolor: "neutral100.main",
                    borderRadius: 2,
                  }}
                >
                  <Chip
                    label={selectedProviderConfig.label}
                    color="primary"
                    size="small"
                    variant="outlined"
                  />
                </Box>

                <Typography variant="body2" color="text.secondary">
                  {selectedProviderConfig.helpText}
                </Typography>

                <Divider />

                <Typography variant="subtitle2" fontWeight={600}>
                  Como deseja configurar a credencial?
                </Typography>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <Card
                    variant="outlined"
                    sx={{
                      flex: 1,
                      borderColor:
                        credentialMode === "reuse" ? "primary.main" : "divider",
                      borderWidth: credentialMode === "reuse" ? 2 : 1,
                      opacity: hasReuseKeys ? 1 : 0.5,
                    }}
                  >
                    <CardActionArea
                      onClick={() =>
                        hasReuseKeys && handleCredentialModeChange("reuse")
                      }
                      disabled={!hasReuseKeys}
                      sx={{ minHeight: 44, p: 1.5 }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <KeyIcon color="action" fontSize="small" />
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            Reutilizar chave existente
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            De outro agente com {selectedProviderConfig.label}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardActionArea>
                  </Card>

                  <Card
                    variant="outlined"
                    sx={{
                      flex: 1,
                      borderColor:
                        credentialMode === "new" ? "primary.main" : "divider",
                      borderWidth: credentialMode === "new" ? 2 : 1,
                    }}
                  >
                    <CardActionArea
                      onClick={handleNewKeyClick}
                      sx={{ minHeight: 44, p: 1.5 }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <VpnKeyIcon color="action" fontSize="small" />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight={600}>
                            Nova chave
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Informar chave · abre o console do provedor
                          </Typography>
                        </Box>
                        <OpenInNewIcon
                          color="action"
                          fontSize="small"
                          sx={{ opacity: 0.6 }}
                        />
                      </Stack>
                    </CardActionArea>
                  </Card>
                </Stack>

                {credentialMode === null && (
                  <Typography variant="caption" color="text.secondary">
                    Selecione uma das opções acima para continuar.
                  </Typography>
                )}

                {credentialMode === "reuse" && hasReuseKeys && (
                  <Box>
                    {!currentApiKey && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mb: 1, display: "block" }}
                      >
                        Selecione a chave que deseja reutilizar.
                      </Typography>
                    )}
                    <RadioGroup
                      value={currentApiKey}
                      onChange={(e) =>
                        setValue("apiKey", e.target.value, {
                          shouldValidate: true,
                        })
                      }
                    >
                      {reuseKeys.map((item) => (
                        <Card
                          key={item.value}
                          variant="outlined"
                          sx={{
                            mb: 1,
                            borderColor:
                              currentApiKey === item.value
                                ? "primary.main"
                                : "divider",
                            borderWidth: currentApiKey === item.value ? 2 : 1,
                          }}
                        >
                          <FormControlLabel
                            value={item.value}
                            control={
                              <Radio sx={{ minWidth: 44, minHeight: 44 }} />
                            }
                            label={
                              <Box sx={{ py: 0.5 }}>
                                <Typography
                                  variant="body2"
                                  fontWeight={600}
                                  fontFamily="monospace"
                                >
                                  {maskApiKey(item.value)}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ mt: 0.75, display: "block" }}
                                >
                                  Utilizada em:
                                </Typography>
                                {item.usedIn.map((label) => (
                                  <Typography
                                    key={label}
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: "block", pl: 0.5 }}
                                  >
                                    · {label}
                                  </Typography>
                                ))}
                              </Box>
                            }
                            sx={{
                              m: 0,
                              width: "100%",
                              px: 1,
                              alignItems: "flex-start",
                            }}
                          />
                        </Card>
                      ))}
                    </RadioGroup>
                  </Box>
                )}

                {credentialMode === "new" && (
                  <FormTextInput
                    name="apiKey"
                    control={control}
                    label="Chave de API"
                    required
                    variant="filled"
                    type="password"
                  />
                )}
              </Stack>
            )}

            <Box pt={3}>
              <FormButtonGroup
                onGoBackButtonClick={handleBack}
                onNextButtonClick={
                  step === "provider"
                    ? () => {
                        resetCredential();
                        setStep("credential");
                      }
                    : handleSubmit(onSubmit)
                }
                loading={isSubmitting}
                disabled={
                  step === "provider"
                    ? !currentProvider
                    : isSubmitting || !canSubmit
                }
                goBackButtonText={step === "provider" ? "Cancelar" : "Voltar"}
                nextButtonText={step === "provider" ? "Continuar" : "Ativar"}
                direction="row-reverse"
                maxWidth
              />
            </Box>
          </DialogContent>
        </form>
      </Dialog>

      {/* ---- Dialog: confirmar redirect para console do provedor ---- */}
      <Dialog
        open={redirectConfirmOpen}
        onClose={() => setRedirectConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" component="span" fontWeight={600}>
            Abrir página de obtenção de chave?
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Você será redirecionado para o console do{" "}
            {selectedProviderConfig?.label ?? "provedor"} para criar ou copiar
            sua chave de API. Deseja continuar?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRedirectConfirmOpen(false)}>Agora não</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (selectedProviderConfig?.apiKeyUrl) {
                window.open(
                  selectedProviderConfig.apiKeyUrl,
                  "_blank",
                  "noopener,noreferrer"
                );
              }
              setRedirectConfirmOpen(false);
            }}
            startIcon={<OpenInNewIcon />}
          >
            Sim, redirecionar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
